import React, { useState, useEffect, useContext, useRef } from "react";
import TopBar from "../../components/TopBar";
import { SessionContext } from "../../components/SessionContext";
import supabase from "../../supabase";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { parseOnlinePaymentMessage } from "../../utils/onlinePaymentParser";

const CreateInvoice = () => {
  const { session } = useContext(SessionContext);
  const [formData, setFormData] = useState({
    order_id: Math.floor(100000 + Math.random() * 900000),
    amount: "",
    template_type: "",
    order_type: "",
    name: "",
    address: "",
    place: "",
    city: "",
    state: "",
    zip: "",
    country: "INDIA",
    phone: "",
    whatsapp: "",
    ebiller_id: "55938",
    product_name: "",
    postoffice_name: "",
    tracking_id: "",
  });

  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [productsList, setProductsList] = useState([]);
  const [pasteText, setPasteText] = useState("");
  const [pasteExpanded, setPasteExpanded] = useState(true);
  const [autofilledFieldsCount, setAutofilledFieldsCount] = useState(0);
  const pasteInputRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase
        .from("products")
        .select("name")
        .order("name", { ascending: true });

      if (!error && data) {
        // Deduplicate if necessary
        const uniqueNames = [...new Set(data.map(item => item.name))];
        setProductsList(uniqueNames);
      }
    };
    fetchProducts();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleApplyPastedText = (rawText) => {
    const text = rawText !== undefined ? rawText : pasteText;
    if (!text || !text.trim()) {
      toast.warning("Please paste an order message first!");
      return;
    }

    const parsed = parseOnlinePaymentMessage(text);
    if (!parsed || parsed.count === 0) {
      toast.error("Could not find any recognized order fields in the pasted text.");
      return;
    }

    const { data, count } = parsed;

    setFormData((prev) => {
      const updated = { ...prev };

      if (data.name) updated.name = data.name;
      if (data.address) updated.address = data.address;
      if (data.place) updated.place = data.place;
      if (data.postoffice_name) updated.postoffice_name = data.postoffice_name;
      if (data.city) updated.city = data.city;
      if (data.state) updated.state = data.state;
      if (data.zip) updated.zip = data.zip;
      if (data.phone) updated.phone = data.phone;
      if (data.whatsapp) updated.whatsapp = data.whatsapp;
      if (data.amount) updated.amount = data.amount;

      // Handle product name and size
      if (data.product_name && data.size) {
        updated.product_name = `${data.product_name} (${data.size})`;
      } else if (data.product_name) {
        updated.product_name = data.product_name;
      } else if (data.size) {
        if (prev.product_name && !prev.product_name.toLowerCase().includes(data.size.toLowerCase())) {
          updated.product_name = `${prev.product_name} (${data.size})`;
        } else if (!prev.product_name) {
          updated.product_name = `Size: ${data.size}`;
        }
      }

      // Default order_type to "New Order" if not selected
      if (!updated.order_type) {
        updated.order_type = "New Order";
      }

      // Default template_type to Prepaid for Online Payment
      if (!updated.template_type || updated.template_type.includes("COD")) {
        if (updated.template_type && updated.template_type.includes("Perfumes")) {
          updated.template_type = "TaqwaPerfumes-Prepaid";
        } else {
          updated.template_type = "TaqwaFashionStore-Prepaid";
        }
      }

      return updated;
    });

    setAutofilledFieldsCount(count);
    toast.success(`Autofilled ${count} fields from Online Payment details!`);
  };

  const handlePasteFromClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText();
        if (text) {
          setPasteText(text);
          handleApplyPastedText(text);
        } else {
          toast.info("Clipboard is empty.");
        }
      } else {
        toast.info("Please paste (Ctrl+V) directly into the box below.");
      }
    } catch (err) {
      console.warn("Clipboard access failed:", err);
      toast.info("Please paste (Ctrl+V) directly into the box below.");
    }
  };

  const handleTextareaChange = (e) => {
    const text = e.target.value;
    setPasteText(text);
  };

  const handleTextareaPaste = (e) => {
    const pasted = e.clipboardData?.getData("text");
    if (pasted) {
      setPasteText(pasted);
      setTimeout(() => {
        handleApplyPastedText(pasted);
      }, 50);
    }
  };

  const handleClearPaste = () => {
    setPasteText("");
    setAutofilledFieldsCount(0);
  };

  const generateInvoiceHTML = () => {
    const storeName = formData.template_type.includes("Perfumes") ? "TAQWA PERFUMES" : "TAQWA FASHION STORE";
    const isPrepaid = formData.template_type.includes("Prepaid");
    const dateStr = moment().format("DD-MM-YYYY");
    const timeStr = moment().format("HH:mm");

    if (!isPrepaid) {
      const productRowHtml = `
        <tr>
          <td>${formData.product_name || 'Manual Order Item'} (1)</td>
          <td style="text-align: center;">${formData.amount || 0}</td>
          <td style="text-align: center; border-right: none;">${formData.amount || 0}</td>
        </tr>
      `;

      return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${formData.order_id}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700;900&family=Roboto:wght@400;700;900&display=swap" rel="stylesheet">

<style>
  @page {
    size: 4in 6in;
    margin: 0.1in;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body {
    width: 3.8in;
    height: 5.8in;
  }

  body {
    font-family: 'Roboto Condensed', 'Roboto', sans-serif;
    font-size: 10pt;
    color: #000;
    background: #fff;
    text-transform: uppercase;
  }

  .label-wrapper {
    width: 100%;
    height: 100%;
    border: 2px solid #000;
    display: flex;
    flex-direction: column;
  }

  .row {
    display: flex;
    width: 100%;
    border-bottom: 1.5px solid #000;
    overflow: hidden;
  }

  .row:last-child {
    border-bottom: none;
  }

  .row-header      { flex: 1.0; }
  .row-pin         { flex: 0.5; }
  .row-shipto      { flex: 5.2; }
  .row-seller-date { flex: 1.8; } 
  .row-products    { flex: 3.2; display: block; }
  .row-orderid     { flex: 0.6; }
  .row-footer      { flex: 0.5; }

  .store-name {
    font-size: 17pt;
    font-weight: 900;
    text-align: center;
    line-height: 1.2;
    width: 100%;
  }

  .header-full {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 8px;
  }

  .pin-left {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    border-right: 1.5px solid #000;
    display: flex;
    align-items: center;
  }

  .route-right {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .shipto-cell {
    width: 62%;
    padding: 4px 8px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
  }

  .shipto-addr-heavy {
    font-size: 10.5pt;
    font-weight: 900;
    line-height: 1.3;
    margin-top: 2px;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .cod-cell {
    width: 38%;
    padding: 7px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: #fff;
  }

  .cod-amount-title {
    font-weight: 900;
    font-size: 10pt; 
    line-height: 1.1;
    margin-bottom: 4px;
    white-space: nowrap;
  }

  .shipto-name {
    font-size: 12pt;
    font-weight: 900;
  }

  .seller-cell {
    width: 65%;
    padding: 7px 9px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .seller-addr-bold {
    font-size: 8.5pt;
    font-weight: 700; 
    line-height: 1.4;
  }

  .product-table {
    width: 100%;
    border-collapse: collapse;
  }

  .product-table th, .product-table td {
    padding: 5px 7px;
    border-right: 1.5px solid #000;
    font-weight: 900;
  }

  .product-table th {
    font-size: 8pt;
    border-bottom: 1.5px solid #000;
    background: #ebebeb;
    text-align: left;
  }

  .product-table td {
    font-size: 9pt;
    border-bottom: 1px solid #ccc;
  }

  .order-id-text {
    font-size: 12pt;
    font-weight: 900;
    letter-spacing: 1px;
    text-align: center;
  }

  .footer-text {
    font-size: 14pt;
    font-weight: 900;
    letter-spacing: 1px;
    text-align: center;
    width: 100%;
  }
</style>
</head>

<body>

<div class="label-wrapper">

  <!-- 1. STORE HEADER -->
  <div class="row row-header">
    <div class="header-full">
      <div class="store-name">${storeName}</div>
    </div>
  </div>

  <!-- 2. PINCODE / ROUTE -->
  <div class="row row-pin">
    <div class="pin-left">PINCODE: ${formData.zip}</div>
    <div class="route-right">COD / SPEEDPOST</div>
  </div>

  <!-- 3. SHIP TO + COD BOX -->
  <div class="row row-shipto">
    <div class="shipto-cell">
      <div>
        <div style="font-size: 8pt; font-weight: 700; margin-bottom: 2px;">SHIP TO:</div>
        <div class="shipto-name">${formData.name}</div>
        <div class="shipto-addr-heavy">
          ${formData.address}<br>
          ${formData.place ? formData.place + '<br>' : ''}
          ${formData.postoffice_name ? 'Postoffice Name: ' + formData.postoffice_name + '<br>' : ''}
          ${formData.city}, ${formData.state}<br>
          PIN: ${formData.zip}
        </div>
      </div>
      <div style="font-size: 13pt; font-weight: 900; margin-top: 4px; line-height: 1.2;">
        PH: ${formData.phone}
      </div>
    </div>

    <div class="cod-cell">
      <div class="cod-amount-title">COD AMOUNT</div>
      <div style="font-size: 20pt; font-weight: 900; line-height: 1;">
        ${formData.amount || 0}
      </div>
      
      <div style="border: 1.5px solid #000; padding: 2px 4px; margin-top: 6px; width: 100%;">
        <div style="font-size: 6.5pt; font-weight: 900; margin-bottom: -2px;">E-BILLER ID</div>
        <div style="font-size: 13pt; font-weight: 900; letter-spacing: 1px;">${formData.ebiller_id}</div>
      </div>
    </div>
  </div>

  <!-- 4. SELLER + ORDER DATE -->
  <div class="row row-seller-date">
    <div class="seller-cell">
      <div style="font-size: 8pt; font-weight: 700; margin-bottom: 2px;">SELLER:</div>
      <div class="shipto-name" style="font-size: 9pt;">${storeName}</div>
      <div class="seller-addr-bold">
        PH: 7012556844<br>
        KANJIRAMATTOM, KERALA<br>
        INDIA 682315
      </div>
    </div>

    <div style="width: 35%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;">
      <div style="font-size: 8pt; font-weight: 700;">ORDER DATE</div>
      <div style="font-size: 9pt; font-weight: 900;">
        ${dateStr}<br>
        ${timeStr}
      </div>
    </div>
  </div>

  <!-- 5. PRODUCT TABLE -->
  <div class="row row-products">
    <table class="product-table">
      <thead>
        <tr>
          <th style="width: 58%;">PRODUCT (QTY)</th>
          <th style="width: 21%; text-align: center;">PRICE</th>
          <th style="width: 21%; text-align: center; border-right: none;">TOTAL</th>
        </tr>
      </thead>

      <tbody>
        ${productRowHtml}

        <tr style="background: #ebebeb;">
          <td style="border-top: 1.5px solid #000;">TOTAL</td>
          <td colspan="2"
              style="text-align: center; border-top: 1.5px solid #000; border-right: none; font-size: 10pt;">
            INR ${formData.amount || 0}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- 6. ORDER ID ROW -->
  <div class="row row-orderid"
       style="display:flex; align-items:center; justify-content:center; flex-direction:column; padding: 4px 0;">
    <div class="order-id-text">
      ORDER ID ${formData.order_id}
    </div>
    ${formData.tracking_id ? `<div style="font-size: 10pt; font-weight: 900; margin-top: 2px;">TRACKING ID: ${formData.tracking_id}</div>` : ''}
  </div>

  <!-- 7. FOOTER ROW -->
  <div class="row row-footer"
       style="display:flex; align-items:center; justify-content:center; border-bottom: none;">
    <div class="footer-text">
      THANK YOU FOR SHOPPING
    </div>
  </div>

</div>

</body>
</html>`;
    } else {
      const productRowHtml = `
      <tr>
        <td>${formData.product_name || 'Manual Order Item'} (1)</td>
        <td style="text-align: center; border-right: none;">
          ${formData.amount || 0}
        </td>
      </tr>
    `;

      return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${formData.order_id}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700;900&family=Roboto:wght@400;700;900&display=swap" rel="stylesheet">

<style>
  @page {
    size: 4in 6in;
    margin: 0.1in;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body {
    width: 3.8in;
    height: 5.8in;
  }

  body {
    font-family: 'Roboto Condensed', 'Roboto', sans-serif;
    font-size: 10pt;
    color: #000;
    background: #fff;
    text-transform: uppercase;
  }

  .label-wrapper {
    width: 100%;
    height: 100%;
    border: 2px solid #000;
    display: flex;
    flex-direction: column;
  }

  .row {
    display: flex;
    width: 100%;
    border-bottom: 1.5px solid #000;
    overflow: hidden;
  }

  .row:last-child {
    border-bottom: none;
  }

  .row-header      { flex: 1.0; }
  .row-pin         { flex: 0.5; }
  .row-shipto      { flex: 5.4; }
  .row-seller-date { flex: 1.8; } 
  .row-products    { flex: 2.7; display: block; }
  .row-orderid     { flex: 0.6; } 
  .row-footer      { flex: 0.6; }

  .store-name {
    font-size: 17pt;
    font-weight: 900;
    text-align: center;
    line-height: 1.2;
    width: 100%;
  }

  .header-full {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 8px;
  }

  .pin-left {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    border-right: 1.5px solid #000;
    display: flex;
    align-items: center;
  }

  .type-right {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .shipto-cell {
    width: 72%; 
    padding: 4px 8px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
  }

  .shipto-addr-heavy {
    font-size: 10.5pt;
    font-weight: 900;
    line-height: 1.3;
    margin-top: 2px;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .prepaid-cell {
    width: 28%;
    padding: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: #f2f2f2;
  }

  .ebiller-box-inner {
    border: 1.5px solid #000;
    padding: 5px 2px;
    width: 100%;
  }

  .ebiller-id {
    font-size: 13pt; 
    font-weight: 900;
    letter-spacing: 1px;
    line-height: 1.1;
  }

  .paid-text-ultra {
    font-weight: 900;
    font-size: 28pt; 
    letter-spacing: 2px;
    line-height: 1;
    margin-top: 8px;
  }

  .order-subtext {
    font-weight: 400;
    font-size: 9.5pt;
    letter-spacing: 1.5px;
    margin-top: 4px;
  }

  .seller-cell {
    width: 65%;
    padding: 7px 9px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .seller-addr-bold {
    font-size: 9pt;
    font-weight: 900; 
    line-height: 1.4;
  }

  .product-table {
    width: 100%;
    border-collapse: collapse;
  }

  .product-table th, .product-table td {
    border-right: 1.5px solid #000;
    padding: 5px 7px;
    font-weight: 900;
  }

  .product-table th {
    font-size: 8pt;
    background: #ebebeb;
    border-bottom: 1.5px solid #000;
    text-align: left;
  }

  .product-table td {
    font-size: 9pt;
    border-bottom: 1px solid #ccc;
  }

  .order-id-box {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    padding: 4px;
  }

  .order-id-text {
    font-size: 12pt;
    font-weight: 900;
    letter-spacing: 1px;
  }

  .footer-text {
    font-size: 16pt;
    font-weight: 900;
    letter-spacing: 1px;
    text-align: center;
    width: 100%;
    line-height: 1;
  }
</style>
</head>

<body>

<div class="label-wrapper">

  <div class="row row-header">
    <div class="header-full">
      <div class="store-name">${storeName}</div>
    </div>
  </div>

  <div class="row row-pin">
    <div class="pin-left">PINCODE: ${formData.zip}</div>
    <div class="type-right">PREPAID / SPEEDPOST</div>
  </div>

  <div class="row row-shipto">
    <div class="shipto-cell">
      <div>
        <div style="font-size: 9pt; font-weight: 900; margin-bottom: 2px;">SHIP TO:</div>
        <div style="font-size: 12pt; font-weight: 900;">${formData.name}</div>
        <div class="shipto-addr-heavy">
          ${formData.address}<br>
          ${formData.place ? formData.place + '<br>' : ''}
          ${formData.postoffice_name ? 'Postoffice Name: ' + formData.postoffice_name + '<br>' : ''}
          ${formData.city}, ${formData.state}<br>
          PIN: ${formData.zip}
        </div>
      </div>
      <div style="font-size: 13pt; font-weight: 900; margin-top: 4px; line-height: 1.2;">
        PH: ${formData.phone}
      </div>
    </div>

    <div class="prepaid-cell">
      <div class="ebiller-box-inner">
        <div style="font-size: 8pt; font-weight: 900;">E-BILLER ID</div>
        <div class="ebiller-id">${formData.ebiller_id}</div>
      </div>

      <div class="paid-text-ultra">PAID</div>
      <div class="order-subtext">ORDER</div>
    </div>
  </div>

  <div class="row row-seller-date">
    <div class="seller-cell">
      <div style="font-size: 9pt; font-weight: 900; margin-bottom: 2px;">
        SELLER:
      </div>

      <div class="seller-addr-bold">
        ${storeName}<br>
        PH: 7012556844<br>
        KANJIRAMATTOM P O ERNAKULAM<br>
        KERALA 682315
      </div>
    </div>

    <div style="width: 35%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;">
      <div style="font-size: 8pt; font-weight: 900;">ORDER DATE</div>

      <div style="font-size: 10pt; font-weight: 900;">
        ${dateStr}<br>
        ${timeStr}
      </div>
    </div>
  </div>

  <div class="row row-products">
    <table class="product-table">
      <thead>
        <tr>
          <th style="width: 65%;">ITEM (QTY)</th>
          <th style="width: 35%; text-align: center; border-right: none;">
            PRICE
          </th>
        </tr>
      </thead>

      <tbody>
        ${productRowHtml}

        <tr style="background: #ebebeb;">
          <td style="border-top: 1.5px solid #000; font-size: 11pt;">
            TOTAL
          </td>

          <td style="text-align: center; border-top: 1.5px solid #000; border-right: none; font-size: 11pt;">
            ${formData.amount || 0}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="row row-orderid">
    <div class="order-id-box" style="flex-direction: column; padding: 4px 0;">
      <div class="order-id-text">
        ORDER ID ${formData.order_id}
      </div>
      ${formData.tracking_id ? `<div style="font-size: 10pt; font-weight: 900; margin-top: 2px;">TRACKING ID: ${formData.tracking_id}</div>` : ''}
    </div>
  </div>

  <div class="row row-footer"
       style="display:flex; align-items:center; justify-content:center; border-bottom: none;">
    <div class="footer-text">
      THANK YOU FOR SHOPPING
    </div>
  </div>

</div>

</body>
</html>`;
    }
  };

  const printDetails = () => {
    let printWindow = window.open("", "_blank");
    const html = generateInvoiceHTML();
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const saveInvoiceToDb = async () => {
    if (!formData.order_type) {
      toast.warning("Please select an Order Type!");
      return null;
    }
    if (!formData.template_type) {
      toast.warning("Please select a Template!");
      return null;
    }
    if (!formData.name || !formData.phone || !formData.amount) {
      toast.error("Please enter Name, Phone, and Amount!");
      return null;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("manual_invoices")
        .insert([{
          order_id: formData.order_id.toString(),
          amount: formData.amount,
          template_type: formData.template_type,
          order_type: formData.order_type,
          name: formData.name,
          address: formData.address,
          place: formData.place,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          phone: formData.phone,
          whatsapp: formData.whatsapp || formData.phone,
          ebiller_id: formData.ebiller_id,
          product_name: formData.product_name,
          postoffice_name: formData.postoffice_name,
          tracking_id: ""
        }])
        .select();

      if (error) {
        console.error("Error saving invoice to Supabase:", error);
        toast.error("Failed to save invoice: " + error.message);
        setSaving(false);
        return null;
      }
      
      toast.success("Invoice saved successfully!");
      setSaving(false);
      return data;
    } catch (err) {
      console.error("Unexpected error saving invoice:", err);
      toast.error("An unexpected error occurred.");
      setSaving(false);
      return null;
    }
  };

  const handleSaveInvoice = async () => {
    const saved = await saveInvoiceToDb();
    if (saved) {
      setTimeout(() => {
        navigate("/created-invoices");
      }, 1000);
    }
  };

  const handleSaveAndPrintInvoice = async () => {
    const saved = await saveInvoiceToDb();
    if (saved) {
      printDetails();
      setTimeout(() => {
        navigate("/created-invoices");
      }, 1000);
    }
  };

  return (
    <>
      <TopBar avatarInfo={session?.user?.user_metadata} />
      <div className="page h-screen overflow-y-scroll p-8 bg-gray-100 pb-40">
        <h1 className="text-2xl font-bold text-blue-500 mb-6 max-w-2xl mx-auto">Create Manual Invoice</h1>

        <div className="bg-white p-6 rounded shadow-md max-w-2xl mx-auto">
          {/* Online Payment Paste Section */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-base font-bold text-gray-800">Online Payment Paste</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Paste WhatsApp order details to automatically fill the invoice fields.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handlePasteFromClipboard}
                  className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded border border-gray-300 transition shadow-sm"
                  title="Paste directly from your clipboard"
                >
                  Paste from Clipboard
                </button>
                <button
                  type="button"
                  onClick={() => setPasteExpanded(!pasteExpanded)}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 font-medium"
                >
                  {pasteExpanded ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {pasteExpanded && (
              <div className="mt-3">
                <textarea
                  ref={pasteInputRef}
                  rows={4}
                  value={pasteText}
                  onChange={handleTextareaChange}
                  onPaste={handleTextareaPaste}
                  placeholder={"Paste WhatsApp order format here...\n\nExample:\nName :: kavin D\nAddress :: 5A,alagu nager ,first Street,\nPlace :: civil aerodrome post\nDistrict :: coimbatore\nState :: tamilnadu\nPincode :: 641014\nWhats app No :: 8489601000\nMobile No :: 8056960127\nSize :: free size"}
                  className="w-full text-xs font-mono p-3 border border-gray-300 rounded focus:border-blue-500 focus:outline-none bg-gray-50/50 resize-y"
                />

                <div className="mt-2.5 flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs">
                    {autofilledFieldsCount > 0 ? (
                      <span className="text-blue-700 font-semibold bg-blue-50 px-2.5 py-1 rounded border border-blue-200 inline-block">
                        Autofilled {autofilledFieldsCount} fields successfully
                      </span>
                    ) : (
                      <span className="text-gray-400 italic">
                        Paste directly into the box (Ctrl+V) or click Paste from Clipboard
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {pasteText && (
                      <button
                        type="button"
                        onClick={handleClearPaste}
                        className="text-xs text-gray-500 hover:text-red-600 font-medium px-2 py-1.5 transition"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleApplyPastedText()}
                      disabled={!pasteText.trim()}
                      className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold text-xs rounded shadow-sm transition"
                    >
                      Autofill Details
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-bold mb-2">Order ID</label>
              <input type="text" name="order_id" value={formData.order_id} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">Order Type</label>
              <select name="order_type" value={formData.order_type} onChange={handleChange} className="w-full px-3 py-2 border rounded bg-orange-50">
                <option value="" disabled>-- Select Order Type --</option>
                <option value="New Order">New Order</option>
                <option value="Replacement">Replacement</option>
                <option value="Address Corruption">Address Corruption</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">Amount</label>
              <input type="number" name="amount" value={formData.amount} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">Select Template</label>
              <select name="template_type" value={formData.template_type} onChange={handleChange} className="w-full px-3 py-2 border rounded bg-blue-50">
                <option value="" disabled>-- Select Template --</option>
                <option value="TaqwaFashionStore-COD">Taqwa Fashion Store - COD</option>
                <option value="TaqwaFashionStore-Prepaid">Taqwa Fashion Store - Prepaid</option>
                <option value="TaqwaPerfumes-COD">Taqwa Perfumes - COD</option>
                <option value="TaqwaPerfumes-Prepaid">Taqwa Perfumes - Prepaid</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">E-Biller ID</label>
              <input type="text" name="ebiller_id" value={formData.ebiller_id} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-bold mb-2">Product Name</label>
              <input
                type="text"
                name="product_name"
                value={formData.product_name}
                onChange={handleChange}
                list="product-names"
                placeholder="Select or type product name"
                className="w-full px-3 py-2 border rounded bg-yellow-50"
              />
              <datalist id="product-names">
                {productsList.map((name, index) => (
                  <option key={index} value={name} />
                ))}
              </datalist>
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-bold mb-2">Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-bold mb-2">Address Line 1</label>
              <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-bold mb-2">Address Line 2 (Place)</label>
              <input type="text" name="place" value={formData.place} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-bold mb-2">Postoffice Name</label>
              <input type="text" name="postoffice_name" value={formData.postoffice_name} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">City</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">State</label>
              <input type="text" name="state" value={formData.state} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">ZIP Code</label>
              <input type="text" name="zip" value={formData.zip} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">Country</label>
              <input type="text" name="country" value={formData.country} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">Phone / Mobile No</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    phone: val,
                    whatsapp: prev.whatsapp ? prev.whatsapp : val,
                  }));
                }}
                placeholder="Enter 10-digit mobile number"
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-bold mb-2">WhatsApp Number</label>
              <input
                type="text"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    whatsapp: val,
                    phone: prev.phone ? prev.phone : val,
                  }));
                }}
                placeholder="Enter 10-digit WhatsApp number"
                className="w-full px-3 py-2 border rounded"
              />
            </div>

          </div>
          <div className="mt-6 flex gap-4 w-full justify-end border-t pt-4">
            <button 
              onClick={handleSaveInvoice} 
              disabled={saving} 
              className="px-6 py-3 bg-green-600 text-white font-bold rounded shadow hover:bg-green-700 disabled:bg-gray-400"
            >
              {saving ? "Saving..." : "Save Invoice"}
            </button>
            <button 
              onClick={handleSaveAndPrintInvoice} 
              disabled={saving} 
              className="px-6 py-3 bg-blue-500 text-white font-bold rounded shadow hover:bg-blue-600 disabled:bg-gray-400"
            >
              {saving ? "Saving..." : "Save & Print Invoice"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateInvoice;
